import { useOnValueChange } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Button } from '../Button';
import { CancelSmall } from '../Icons';
import { Portal } from '../Portal';
import { Scrim } from '../Scrim';

import { ModalContent } from './components';
import { ModalContextProvider } from './ModalContext';

export type ModalSize = 'medium' | 'large' | 'xlarge' | 'xxlarge';

interface Props {
  title?: string;
  description?: React.ReactNode;
  isOpen: boolean;
  onClose?: () => void;
  size?: ModalSize;
  shouldReturnFocusAfterClose?: boolean;
  modalClosedCallback?: () => void;
  hasCloseButton?: boolean;
  shouldCloseOnEsc?: boolean;
  shouldCloseOnOverlayClick?: boolean;
  shouldCloseOnLocationChange?: boolean;
  sheetModalId?: string;
  onUnmount?: () => void;
  descriptionDataTestId?: string;
  sheetDataTestId?: string;
  closeDataTestId?: string;
}

export const Modal = ({
  title,
  description,
  isOpen,
  onClose,
  size = 'large',
  modalClosedCallback,
  hasCloseButton = true,
  children,
  shouldCloseOnOverlayClick = true,
  shouldReturnFocusAfterClose = true,
  shouldCloseOnLocationChange = true,
  sheetModalId,
  descriptionDataTestId,
  sheetDataTestId,
  closeDataTestId,
  shouldCloseOnEsc = true,
  onUnmount,
}: React.WithChildren<Props>) => {
  const location = useLocation();
  const { t } = useTranslation(['common']);
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    return () => onUnmount?.();
  }, [onUnmount]);

  const onLocationChange = React.useCallback(() => {
    if (shouldCloseOnLocationChange && isOpen) {
      onClose?.();
    }
  }, [shouldCloseOnLocationChange, isOpen, onClose]);

  useOnValueChange(location.key, onLocationChange);

  return (
    <Portal>
      <div role="dialog" aria-modal="true">
        <ModalContextProvider isOpen={isOpen} shouldCloseOnEsc={shouldCloseOnEsc} onClose={onClose} contentRef={contentRef}>
          <Scrim
            isVisible={isOpen}
            testId="modalBackdrop"
            onClick={shouldCloseOnOverlayClick ? onClose : undefined}
            onClosed={modalClosedCallback}
          />
          <ModalContent contentLabel={title} shouldReturnFocusAfterClose={shouldReturnFocusAfterClose} size={size}>
            {isDefined(title) && (
              <div className="z-modal bg-white pb-2 pl-6 pr-10 pt-6">
                <h2 data-testid="modalNameHeader" className="break-words heading-2">
                  {title}
                </h2>
              </div>
            )}
            <div
              ref={contentRef}
              id={sheetModalId}
              data-testid={sheetDataTestId}
              className="scrollbar-always relative mb-5 overflow-auto bg-white px-6 pt-2"
            >
              {isDefined(description) && description !== false && (
                <p data-testid={descriptionDataTestId} className="pb-6 text-neutral-40 body-2">
                  {description}
                </p>
              )}
              {children}
            </div>
            {hasCloseButton && (
              <Button
                aria-label={t('common:close_modal')}
                color="secondary"
                variant="ghost"
                onClick={onClose}
                data-testid={closeDataTestId}
                className="absolute right-4 top-4 z-modal"
                size="medium"
                icon={CancelSmall}
              />
            )}
          </ModalContent>
        </ModalContextProvider>
      </div>
    </Portal>
  );
};
